import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { ProOnly } from '../label/ProOnly';
import { NekoIcon } from '../misc/Icon';
import { useClasses } from '../hooks';
import NekoBusyOverlay from '../common/NekoBusyOverlay';
import { NekoButton } from '../button/Button';
import { nekoStringify } from '../helpers';

const TabsHeaderContainer = Styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  height: 39px;
`;

const TabsHeader = Styled.div`
  display: flex;
  height: 39px;
  position: absolute;
  overflow: hidden;
  max-width: ${props => props.$needsMoreSpace ? `calc(100% - 75px - ${props.$actionWidth}px)` : '100%'};
`;

const TabsActions = Styled.div`
  z-index: 1;
  display: flex;
  align-items: center;
  height: 39px;
  margin-left: auto;
`;

const TabsSlider = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 39px;
  margin-right: 5px;
`;

const Tab = Styled.button`
  border-radius: 8px 8px 0px 0px;
  border: 0;
  background-color: var(--neko-main-color-disabled);
  color: rgb(255 255 255 / 65%);
  display: flex;
  align-items: center;

  cursor: pointer;
  margin: 0 5px 0 0;
  text-align: left;
  padding: 12px 15px 12px 15px;

  &:not(.active):not(.disabled):hover {
    filter: brightness(1.1);
  }

  &:focus {
    outline: none;
  }

  &.active {
    background-color: var(--neko-main-color);
    color: var(--neko-white);
  }

  &.disabled {
    cursor: default;
    display: inline-flex;
    padding-bottom: 7px;
  }

  &.hidden {
    display: none;
  }

  &.inversed {
    &.active {
      background-color: var(--neko-white);
      color: var(--neko-font-color);
    }
  }
`;

const TabContent = Styled.div`
  background-color: var(--neko-main-color);
  color: white;
  display: none;
  padding: 10px;
  border-radius: 0px 0px 8px 8px;

  &.active {
    display: block;
  }

  &.inversed {
    background-color: var(--neko-white);
    color: var(--neko-black);
  }
`;

const getKeyOfTab = (child, i) => {
  const props = child.props;
  let key = "tab-" + (i + 1);
  if (child.key) {
    key = child.key;
  } else if (typeof props.title === 'string') {
    key = props.title.toLowerCase().replace(/[^a-z0-9\u4e00-\u9fa5\u3040-\u309f\u30a0-\u30ff\u2e80-\u2eff\u31f0-\u31ff\u3200-\u32ff\u3400-\u4dbf\uf900-\ufaff ]/gi, '');
    key = key.replace(/ /g, '-');
  }
  return key;
};

const randomHash = (length = 6) => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let hash = '';
  for (let i = 0; i < length; i++) {
    hash += chars[Math.floor(Math.random() * chars.length)];
  }
  return hash;
};

const StyledNekoTabs = props => {
  const { inversed, children, action, ...rest } = props;
  const tabsHeaderRef = useRef(null);
  const tabsContentRef = useRef(null);
  const actionsRef = useRef(null);
  const [childrenKeys, setChildrenKeys] = useState([]);
  const [tabAttributes, setTabAttributes] = useState([]);
  const [initialWidth, setInitialWidth] = useState(0);
  const [actionWidth, setActionWidth] = useState(0);
  const [needsMoreSpace, setNeedsMoreSpace] = useState(false);
  const [renderChildTabs, setRenderChildTabs] = useState(false);
  const tabRefs = useRef([]);

  useLayoutEffect(() => {
    if (tabsContentRef.current) {
      setInitialWidth(tabsContentRef.current.offsetWidth);
      setRenderChildTabs(true);
    }
    if (actionsRef.current) {
      setActionWidth(actionsRef.current.offsetWidth);
    }
  }, []);

  useLayoutEffect(() => {
    const totalWidth = tabRefs.current.reduce((sum, tabRef) => {
      return sum + (tabRef ? tabRef.offsetWidth + 10 : 0);
    }, 0);
    if (totalWidth > (initialWidth - actionWidth)) {
      setNeedsMoreSpace(true);
    }
    else {
      setNeedsMoreSpace(false);
    }
  }, [childrenKeys, initialWidth, actionWidth]);

  const childrenProp = useMemo(() => (children ? (children.length ? children.filter(x => !!x) : [children]) : []), [children]);


  useEffect(() => {
    const allKeys = [];
    const freshChildrenKeys = React.Children.map(childrenProp, (child, i) => {
      let key = getKeyOfTab(child, i);
      if (allKeys.indexOf(key) !== -1) {
        console.log("NekoTabs: Duplicated key found!", key, child);
        key = key + '-' + randomHash();
      }
      allKeys.push(key);
      return key;
    });
    if (freshChildrenKeys.length !== childrenKeys.length) {
      setChildrenKeys(freshChildrenKeys);
      return;
    }
  }, [childrenProp]);

  useEffect(() => {
    const allKeys = [];
    const freshTabAttributes = React.Children.map(childrenProp, (child, i) => {
      const title = child.props.title || ("Untitled Tab " + (i + 1));
      let key = getKeyOfTab(child, i);
      if (allKeys.indexOf(key) !== -1) {
        console.log("NekoTabs: Duplicated key found!", key, child);
        key = key + '-' + randomHash();
      }
      allKeys.push(key);
      const onClick = child.props.onClick ? child.props.onClick : null;
      const requirePro = !props.isPro && (child.props.requirePro || false);
      const hidden = child.props.hidden || false;
      const icon = child.props.icon || false;
      return { key, title, onClick, requirePro, hidden, icon };
    });
    if (nekoStringify(freshTabAttributes, null, true) !== nekoStringify(tabAttributes, null, true)) {
      setTabAttributes(freshTabAttributes);
      return;
    }
  }, [childrenProp, props.isPro]);

  const [currentTab, setCurrentTab] = useState(props.currentTab ?? childrenKeys[0]);
  const classNames = useClasses('neko-tabs', { inversed: inversed });

  useEffect(() => {
    if (props.currentTab && props.currentTab !== currentTab) {
      if (childrenKeys.includes(props.currentTab)) {
        setCurrentTab(props.currentTab);
      } else {
        setCurrentTab(childrenKeys[0]);
      }
    }
    if (!childrenKeys.includes(currentTab)) {
      setCurrentTab(childrenKeys[0]);
    }
  }, [props.currentTab, childrenKeys, currentTab]);
  

  useLayoutEffect(() => {
    if (props.keepTabOnReload) {
      const url = new URL(window.location.href);
      const title = url.searchParams.get('nekoTab');
      if (!title) {
        setCurrentTab(childrenKeys[0]);
        if (props.callOnTabChangeFirst) {
          onTabClick(0, tabAttributes[0]);
        }
        return;
      }

      const searchTitle = decodeURIComponent(title);
      const index = childrenKeys.indexOf(searchTitle);
      if (index === -1) {
        setCurrentTab(childrenKeys[0]);
        if (props.callOnTabChangeFirst) {
          onTabClick(0, tabAttributes[0]);
        }
        return;
      }

      setCurrentTab(childrenKeys[index]);
      if (props.callOnTabChangeFirst) {
        onTabClick(index, tabAttributes[index]);
      }
    }
    else if (props.callOnTabChangeFirst) {
      const index = childrenKeys.indexOf(currentTab);
      onTabClick(index, tabAttributes[index]);
    }
  }, [props.keepTabOnReload, props.callOnTabChangeFirst,
      childrenKeys, currentTab, tabAttributes
  ]);

  const keepTab = useCallback((key) => {
    if (history.pushState && typeof key === 'string') {
      let searchParams = new URLSearchParams(window.location.search);
      searchParams.set('nekoTab', key);
      let newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?' + searchParams.toString();
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }, []);

  const onTabClick = useCallback((tabIndex, tabAttr, ev) => {
    if (!tabAttr || tabAttr.requirePro) {
      return;
    }
    setCurrentTab(tabAttr.key);
    if (props.onChange) {
      props.onChange(tabIndex, tabAttr, ev);
    }
    if (props.keepTabOnReload) {
      keepTab(tabAttr.key);
    }
  }, [props, keepTab]);

  const childTabs = React.Children.map(childrenProp, (child, i) => {
    const isActive = currentTab === getKeyOfTab(child, i);
    const hidden = child.props.hidden;
    if (isActive && hidden) {
      setCurrentTab(childrenKeys[0]);
    }
    return React.cloneElement(child, { isActive: isActive && !hidden, inversed });
  });

  const handleScrollLeft = () => {
    if (tabsHeaderRef.current) {
      tabsHeaderRef.current.scrollTo({
        left: tabsHeaderRef.current.scrollLeft - 200,
        behavior: 'smooth',
      });
    }
  };

  const handleScrollRight = () => {
    if (tabsHeaderRef.current) {
      tabsHeaderRef.current.scrollTo({
        left: tabsHeaderRef.current.scrollLeft + 200,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div ref={tabsContentRef} className={classNames} {...rest}>
      <TabsHeaderContainer>
        <TabsHeader ref={tabsHeaderRef} $needsMoreSpace={needsMoreSpace} $actionWidth={actionWidth}>
          {renderChildTabs && tabAttributes.map((attr, i) => (
            <Tab
              key={attr.key}
              ref={(el) => (tabRefs.current[i] = el)}
              onClick={(ev) => onTabClick(i, attr, ev)}
              className={`neko-tab-title ${attr.key === currentTab ? 'active' : ''} ${attr.requirePro ? 'disabled' : ''} ${attr.hidden ? 'hidden' : ''} ${inversed ? 'inversed' : ''}`}>
              {attr.icon && (
                <NekoIcon icon={attr.icon} width={15} height={15} style={{ marginRight: '2px' }} raw />
              )}
              {attr.title}
              <ProOnly
                className="inline"
                show={attr.requirePro}
                style={{ marginLeft: 10, marginRight: -5, top: -1 }}
              />
            </Tab>
          ))}
        </TabsHeader>
        {(!!action || needsMoreSpace) && <TabsActions ref={actionsRef}>
          {needsMoreSpace && <TabsSlider>
            <NekoButton rounded className="secondary" icon="chevron-left" onClick={handleScrollLeft}></NekoButton>
            <NekoButton rounded className="secondary" icon="chevron-right" onClick={handleScrollRight}></NekoButton>
          </TabsSlider>}
          {!!action && action}
        </TabsActions>}
      </TabsHeaderContainer>
      {childTabs}
    </div>
  );
};

const StyledNekoTab = props => {
  const { children, isActive = false, busy = false, inversed } = props;
  const classNames = useClasses('neko-tab-content', { active: isActive, inversed: inversed });

  return (
    <NekoBusyOverlay busy={busy}>
      <TabContent className={classNames}>{!!isActive && children}</TabContent>
    </NekoBusyOverlay>
  );
};

const NekoTabs = props => <StyledNekoTabs {...props} />;

NekoTabs.propTypes = {
  isPro: PropTypes.bool,
  onChange: PropTypes.func,
  action: PropTypes.node,
  currentTab: PropTypes.string,
  keepTabOnReload: PropTypes.bool,
  callOnTabChangeFirst: PropTypes.bool,
};

const NekoTab = props => <StyledNekoTab {...props} />;

NekoTab.propTypes = {
  isActive: PropTypes.bool,
  requirePro: PropTypes.bool,
  title: PropTypes.string,
  icon: PropTypes.string,
};

export { NekoTab, NekoTabs };
